import { request } from '@ps/web-core-api';
import type { HttpHeaders, RequestError, RequestOptions, StompHeaders } from '@ps/web-core-api';
import { canUseDOM } from '@ps/web-core-utils';
import { merge } from 'lodash';

import type { Store } from '../types';

import { WMTConnection } from './WMTConnection';

export const bindRequest =
  (store: Store): typeof request =>
  (options: RequestOptions) => {
    const connection = WMTConnection(store);
    const { select, dispatch, getState } = store;
    const rootState = getState();
    const embedded = select.device.isEmbedded(rootState);
    const device = select.device.getId(rootState);
    const os = select.device.getOS(rootState);
    const authorization = select.session.getWMTAuthorization(rootState);
    const currency = select.currency.get(rootState);
    const locale = select.locale.get(rootState);
    const { href, origin, host } = select.location.get(rootState);
    const platform = select.device.getPlatform(rootState);
    const brand = `${select.cms.getWebsiteBrandId(rootState)}` as const;
    const site = `${select.cms.getSiteId(rootState)}` as const;
    const region = select.cms.getRegion(rootState);
    const country = select.country.getCountry(rootState);
    const state = select.country.getState(rootState);
    const headers: HttpHeaders = { referer: href, ['x-fi-ps-platform-id']: `${platform}` };
    const isSports = options.service.startsWith('/sports');

    if (authorization) headers.authorization = authorization;

    if (options.http) {
      headers['x-stars-currency'] = currency;
      headers['x-stars-locale'] = locale;
      headers['x-stars-brand'] = brand;
      headers['x-stars-site'] = site;
    } else {
      Object.assign(headers, { currency, locale, region, brand, site } satisfies StompHeaders);
    }

    if (!canUseDOM) {
      options.httpDomain = origin;
      headers.origin = origin;
      headers['x-stars-geo-country'] = country;
      headers['x-stars-geo-state'] = state;
    }

    if (!canUseDOM && options.http) {
      headers['x-stars-embedded'] = embedded ? '1' : '0';
      headers['x-device'] = `${device}`;
      headers['x-os-family'] = `${os}`;
      headers['x-stars-currency'] = currency;
      headers['x-stars-locale'] = locale;
      headers['x-forwarded-host'] = host;
    } else if (!canUseDOM) {
      const stompHeaders = headers as StompHeaders;
      stompHeaders.device = `${device}`;
      stompHeaders.os = `${os}`;
      stompHeaders.locale = locale;
      stompHeaders.embedded = embedded ? '1' : '0';
      stompHeaders['x-forwarded-host'] = host;
    }

    try {
      return request(merge({ headers, connection }, options));
    } catch (e) {
      const error = e as RequestError;

      if (isSports && error.body.code === 'MAINTENANCE') {
        dispatch.system.update({ body: { sports: 'red' } });
      }

      if (isSports && error.statusCode === 401) {
        const dispatchLoggedOutModal = () => dispatch.modal.enqueue({ path: '/sports/logged-out' });
        if (embedded) {
          document.addEventListener('starslogout', dispatchLoggedOutModal, { once: true });
          // window.starsweb?.auth.logout(); TODO types for starsweb embedded API
        } else {
          dispatch.auth.logout();
          dispatchLoggedOutModal();
        }
      }

      throw e;
    }
  };
